// Copyright © 2021 Move Closer

import { IWindow } from '@movecloser/front-core'
import { Component, Inject, Prop, Vue } from 'vue-property-decorator'
import NoSSR from 'vue-no-ssr'
import VueEasyLightbox from 'vue-easy-lightbox'

import { BootstrapBadge, BootstrapButton, BootstrapIcon, BootstrapImage, BootstrapLink } from '../../../dsl/front'
import { CreatesPdf, ShareType } from '../../../services'

import { AppTheme, Image, ImageFile, ImageRatio } from '../../_contracts'
import { prepareImage } from '../../_filters'

import { AdBannerModuleUi } from '../../AdBanner/ui'
import { IActionButton } from '../ActionButton'
import { ArticleActions } from '../ArticleActions'
import { ArticleShare } from '../ArticleShare'
import { ArticleSocials } from '../ArticleSocials'
import { Gallery } from '../Gallery'
import { ImageWithCaption } from '../ImageWithCaption'
import { LineHeader } from '../LineHeader/ui'
import { PlayIcon } from '../PlayIcon'
import { ReadingTime } from '../ReadingTime'

import { ArticleHeaderData, ArticleHeaderVideoProps, VideoConfig } from './ArticleHeader.contracts'
import { ArticleHeaderImage } from './partials/ArticleHeaderImage'
import { ArticleHeaderVideo } from './partials/ArticleHeaderVideo'
import { ArticleAuthorBlock } from './partials/ArticleAuthorBlock'

/**
 * @author Sebastian Dziechciarz <sebastian.dziechciarz@movecloser.pl>
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 * @author Olga Milczek <olga.milczek@movecloser.pl>
 * @author Agnieszka Zawadzka <agnieszka.zawadzka@movecloser.pl>
 */
@Component<ArticleHeader>({
  name: 'ArticleHeader',
  components: {
    AdBannerModuleUi,
    ArticleActions,
    ArticleAuthorBlock,
    ArticleHeaderVideo,
    ArticleHeaderImage,
    ArticleShare,
    ArticleSocials,
    BootstrapBadge,
    BootstrapButton,
    BootstrapIcon,
    BootstrapImage,
    BootstrapLink,
    Gallery,
    ImageWithCaption,
    LineHeader,
    NoSSR,
    PlayIcon,
    ReadingTime,
    VueEasyLightbox
  },

  template: `
    <header class="article-header" :class="{ 'article-header--video': hasVideo }">
      <ul class="article-header__labels list-inline">
        <li v-if="sponsored" class="article-header__label">
          <BootstrapBadge>sponsorowane</BootstrapBadge>
        </li>
        <li v-for="(label, index) in content.labels" :key="label.name + index">
          <BootstrapBadge :theme="label.color">
            {{ label.name }}
          </BootstrapBadge>
        </li>
      </ul>

      <h1 class="article-header__title">{{ content.title }}</h1>

      <ArticleAuthorBlock
        v-if="content.author"
        :author="content.author"
        :publicationDate="content.publicationDate"
        :hideDate="content.options && content.options.hidePublicationDate"
        :pdfAction="pdfAction"
        :isMobile="isMobile"
      />

      <div v-if="content.lead" v-html="content.lead" class="article-header__lead" />

      <!--Cover image / Gallery -->
      <Gallery
        v-if="hasGallery"
        class="article-header__gallery"
        :images="images"
        :maxWidth="maxWidth"
        :windowService="windowService"
        fullScreen
      />
      <!-- END | Cover image / Gallery -->

      <!--Cover image / Single image -->
      <ArticleHeaderImage
        v-else-if="hasThumbnail || fallbackImage"
        :caption="hasVideo ? undefined : overridenCaption"
        :hideDescription="hasVideo || shouldHideDescription"
        :hideAuthor="hasVideo"
        :isLazy="isLazy"
        :thumbnail="thumbnail"
        :fallbackImage="fallbackImage"
      >
        <template v-slot:video v-if="hasVideo">
          <ArticleHeaderVideo
            :config="videoProps"
            @pause="payload => $emit('pause', payload)"
            @play="payload => $emit('play', payload)"
          />
        </template>

        <template v-slot:ad>
          <slot name="ad" />
        </template>
      </ArticleHeaderImage>
      <!-- END | Cover image -->

      <!-- Print, download & social buttons -->
      <div class="article-header__actions__wrap">
        <ArticleActions class="article-header__actions" v-bind="{ actions }" />
      </div>

      <!-- ld+json -->
      <slot />
    </header>
  `
})
export class ArticleHeader extends Vue {
  @Prop({ type: Object, required: true })
  private readonly content!: ArticleHeaderData

  @Prop({ type: String, required: false, default: '' })
  public readonly fallbackImage!: string

  @Prop({ type: Boolean, required: false, default: true })
  protected readonly isMobile!: boolean

  @Prop({ type: Boolean, required: false, default: false })
  protected readonly isLazy!: boolean

  @Prop({ type: Object, required: true })
  protected readonly videoConfig!: VideoConfig

  @Prop({ type: Object, required: false, default: null })
  protected readonly pdfAction!: IActionButton | null

  @Prop({ type: Array, required: true })
  protected readonly actions!: IActionButton[]

  @Prop({ type: Object, required: true })
  public readonly windowService!: IWindow

  @Inject({ from: 'theme', default: AppTheme.Default })
  public readonly theme!: AppTheme

  public readonly AppTheme = AppTheme

  public maxWidth: number | undefined = this.isMobile ? 768 : 800

  public get hasGallery (): boolean {
    return typeof this.content.cover === 'object' && Array.isArray(this.content.cover.gallery) && this.content.cover.gallery !== null
  }

  public get hasVideo (): boolean {
    return !!this.content.cover.video && (
      (typeof this.content.cover.video.url === 'string' && this.content.cover.video.url.length > 0) ||
      (typeof this.content.cover.video.onNetworkUrl === 'string' && this.content.cover.video.onNetworkUrl.length > 0) ||
      (!!this.content.cover.video.file && typeof this.content.cover.video.file === 'object' &&
        (!!this.content.cover.video.file.url || !!this.content.cover.video.file.m3u8))
    )
  }

  public get thumbnail (): Image | null {
    if (!this.content.cover.image) {
      return null
    }

    return prepareImage(this.content.cover.image, ImageRatio.Standard169, this.maxWidth, this.isLazy)
  }

  public get hasThumbnail (): boolean {
    return this.thumbnail !== null
  }

  public get overridenCaption (): string | undefined {
    return (typeof this.content.cover === 'object' && this.content.cover.override === true) ? this.content.cover.caption : undefined
  }

  public get caption (): string {
    return (this.overridenCaption ? this.overridenCaption : this.thumbnail?.caption) ?? ''
  }

  public get images (): ImageFile[] {
    if (!this.hasGallery) {
      return []
    }

    if (!this.content.cover.gallery) {
      return []
    }

    return this.content.cover.gallery
  }

  public get shouldHideDescription (): boolean {
    if (!this.content.cover.hideDescription) {
      return false
    }

    return this.content.cover.hideDescription
  }

  public get videoProps (): ArticleHeaderVideoProps {
    return {
      ...this.videoConfig,
      name: (this.caption || this.content.meta.title) ?? '',
      settings: this.content.cover.videoSettings,
      thumbnail: this.thumbnail,
      uploadDate: this.content.publicationDate,
      video: this.content.cover.video
    }
  }

  public get sponsored (): boolean {
    return this.content.options.sponsored
  }

  public onSocialClick (social: ShareType) {
    this.$emit('social-click', social)
  }
}

export default ArticleHeader
